/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import Parse from 'parse';
import * as Sentry from '@sentry/browser';

import * as Config from '../config.json';
import { ApiService } from './api-service';
import {
  Employee,
  IndividualAccount,
  AdminInviteParams,
  PaginatedUsers,
  User,
  AddEmployerAdminParams,
} from './types';
import { User as AdminUserType } from 'portal/admin/types';
import type { UnlinkStripeAccountResponse } from 'portal/admin/unlink-stripe-types';
import type {
  DeleteStripeAccountResponse,
  DeleteStripeTarget,
} from 'portal/admin/delete-stripe-types';
import type {
  AuditLogEntry,
  AuditLogListResponse,
  AuditLogSearchBy,
} from 'portal/admin/audit-log-types';
import type {
  EmployerTeamRecordsResponse,
  GetEmployerTeamRecordsParams,
} from 'portal/admin/employer-team-stripe-types';
import { EMPLOYER_TEAM_ALL_RECORDS_PAGE_SIZE } from 'portal/admin/employer-team-stripe-types';
import moment from 'moment';

export type AdminPaginatedUsers = {
  users: AdminUserType[];
  meta: PaginatedUsers['meta'];
};

export class AdminApiService {
  static async init() {
    Parse.initialize(Config.parse.appId, Config.parse.javascriptKey);
    Parse.serverURL = Config.parse.serverURL;
  }

  static getServerUrl() {
    return Config.parse.serverURL;
  }

  static async isAdmin(user: any) {
    const roleQuery = new Parse.Query(Parse.Role);
    roleQuery.equalTo('users', user);
    try {
      const roles = await roleQuery.find();
      if (roles.find((role) => role.getName() === 'super-admin')) {
        return true;
      }
    } catch (error) {
      switch (error.code) {
        case Parse.Error.INVALID_SESSION_TOKEN:
          Parse.User.logOut();
          window.location.reload();
          break;
        default:
          alert('Error ' + error.message);
          Sentry.captureException(error);
          break;
      }
    }

    return false;
  }

  static async isCompany(employer: any) {
    const Employer = Parse.Object.extend('Employer');
    const employerData = await new Parse.Query(Employer).get(employer.id);

    return employerData.get('employerType') === 'Company';
  }

  static async getUsers() {
    return Parse.Cloud.run('getAllUsers');
  }

  static async getUserEmployers(userId: string) {
    return Parse.Cloud.run('getUserEmployers', {
      userId,
    });
  }

  static async getSuperAdminEmployers(userId: string) {
    return Parse.Cloud.run('getSuperAdminEmployers', {
      userId,
    });
  }

  static async getPaginatedUsers(
    pageNumber: number,
    pageSize: number,
    username?: string,
    employerName?: string,
    firstName?: string,
    lastName?: string
  ): Promise<AdminPaginatedUsers> {
    if (Number.isNaN(pageNumber)) {
      pageNumber = 1;
    }

    if (Number.isNaN(pageSize) || pageSize > 1000 || pageSize < 50) {
      pageSize = 50;
    }

    const result: PaginatedUsers = await Parse.Cloud.run('getPaginatedUsersTest', {
      pageNumber,
      pageSize,
      searchBy: {
        username,
        employerName,
        firstName,
        lastName
      },
    });

    const users = result.users.map((dto: any) => {
      // DTO structure: { user, employer, employerPrivate, recordType }
      const user = dto.user;
      const employer = dto.employer;
      const employerPrivate = dto.employerPrivate;
      const recordType = dto.recordType;

      const firstName = user?.firstname ?? '';
      const lastName = user?.lastname ?? '';
      const isStripeLinked = recordType === 'user' 
        ? user?.isStripeLinked 
        : employerPrivate?.isStripeLinked;

      return {
        userId: user?.objectId || user?.id,
        employerName: employer?.name ?? 'No employer',
        employer,
        employerPrivate,
        email: user?.username ?? user?.email ?? '',
        created: user?.createdAt,
        isSubCompany: !!user?.isSubCompany,
        userLevel: user?.level,
        firstname: firstName,
        lastname: lastName,
        fullName: `${firstName ? firstName : ''} ${lastName ? lastName : ''}`,
        isStripeLinked: user?.isStripeLinked ?? false,
        status: isStripeLinked ? 'linked' : 'pending',
        stripeStatus: recordType === 'user' 
          ? user?.stripeStatus 
          : employerPrivate?.stripeStatus,
      };
    });

    return {
      ...result,
      users,
    };
  }

  static async getEmployerTeamRecords(
    params: GetEmployerTeamRecordsParams
  ): Promise<EmployerTeamRecordsResponse> {
    const pageNumber = Number.isNaN(params.pageNumber) ? 1 : params.pageNumber ?? 1;
    let pageSize = params.pageSize ?? 50;

    if (Number.isNaN(pageSize) || pageSize < 1) {
      pageSize = 50;
    }
    if (pageSize > EMPLOYER_TEAM_ALL_RECORDS_PAGE_SIZE) {
      pageSize = EMPLOYER_TEAM_ALL_RECORDS_PAGE_SIZE;
    }

    const payload: Record<string, unknown> = {
      pageNumber,
      pageSize,
    };

    if (params.employerIds?.length) {
      payload.employerIds = params.employerIds;
    }

    if (params.includeRoles?.length) {
      payload.includeRoles = params.includeRoles;
    }

    if (params.searchBy) {
      payload.searchBy = params.searchBy;
    }

    try {
      return await Parse.Cloud.run('getPaginatedUsersForEmployerAdmin', payload);
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getPaginatedUsersForEmployerAdmin(
    pageNumber: number,
    pageSize: number,
    username?: string,
    employerName?: string,
    firstName?: string,
    lastName?: string,
    status?: string,
    role?: string,
  ): Promise<AdminPaginatedUsers> {
    if (Number.isNaN(pageNumber)) {
      pageNumber = 1;
    }

    if (Number.isNaN(pageSize) || pageSize > 1000 || pageSize < 50) {
      pageSize = 50;
    }

    const result: PaginatedUsers = await Parse.Cloud.run('getPaginatedUsersForEmployerAdmin', {
      pageNumber,
      pageSize,
      searchBy: {
        username,
        employerName,
        firstName,
        lastName,
        status,
        role
      },
    });

    const users = result.users.map((user) => {
      const employer = user.get('employer');
      const firstName = user.get('firstname') ?? '';
      const lastName = user.get('lastname') ?? '';
      const employerPrivate = user.get('employerprivate');

      return {
        userId: user.id,
        employerName: employer?.get('name') ?? 'No employer',
        employer,
        employerId: employer?.id,
        employerPrivate,
        email: user.get('username'),
        created: user.createdAt,
        isSubCompany: user.get('sub_company') ?? false,
        userLevel: user.get('level'),
        firstname: firstName,
        lastname: lastName,
        fullName: `${firstName} ${lastName}`,
        jobTitle: (user as any).get('jobTitle'),
        phone: (user as any).get('phone'),
        roleLevel: (user as any)?.get('roleLevel'),
        roleLabel: (user as any)?.get('roleLabel'),
        status:
          user.get('stripeId') || employerPrivate?.get('stripeId')
            ? 'linked'
            : 'pending',
      };
    });

    return {
      ...result,
      users,
    };
  }

  static async getAllTips(begin: any, end: any, employer: any) {
    return Parse.Cloud.run('getAllTips', { begin, end, employer });
  }

  static async getAllFeedback(begin: any, end: any, employerId: any) {
    return Parse.Cloud.run('getAllFeedback', { begin, end, employerId });
  }

  static async getAllCharges(begin: any, end: any, employerId: any, limit: number ) {
    return Parse.Cloud.run('getAllCharges', { begin, end, employerId, limit });
  }

  static async getFirstAndLastCharge(employerId: any ) {
    return Parse.Cloud.run('getFirstAndLastCharge', { employerId });
  }

  static async getEmployerChargeIdleTimeReport(reportingDate = new Date()) {
    return Parse.Cloud.run('getEmployerChargeIdleTimeReport', { reportingDate });
  }

  static async createEmployer(
    name: string,
    password: string,
    tier: string,
    employerType: string,
    email: string,
    industry: string
  ) {
    await Parse.Cloud.run('createEmployer', {
      name,
      password,
      tier,
      employerType,
      email: email.toLowerCase(),
      industry,
    });
  }

  static async getEmployer(employerId: string) {
		const Employer = Parse.Object.extend('Employer');
		const employerQuery = new Parse.Query(Employer);

    return employerQuery.get(employerId);
  }

  static async createEmployerDepartment(
    name: string,
    iconType: string,
    employerId: string,
  ) {
    const Employer = Parse.Object.extend('Employer');
    const Department = Parse.Object.extend('Department');

    const employer = new Employer();
    employer.id = employerId;

    const employerDepartment = new Department();
    employerDepartment.set('employer', employer);
    employerDepartment.set('name', name);
    employerDepartment.set('iconType', iconType);

    console.log('save department..', name, employerId, employerDepartment);

    try {
      const savedDepartment = await employerDepartment.save();
      console.log('department saved', savedDepartment);
    } catch (e) {
      console.log('department save error', e);
      throw new Error(e);
    }
  }

  static async getDepartments(employerId: string) {
    const Employer = Parse.Object.extend('Employer');
    const Department = Parse.Object.extend('Department');
    const departmentQuery = new Parse.Query(Department);

    const employer = new Employer();
    employer.id = employerId;

    departmentQuery.include('employer');
    departmentQuery.doesNotExist('deletedAt');
    departmentQuery.equalTo('employer', employer);
    let departmentsForEmployer: any[] = [];

    try {
      departmentsForEmployer = await departmentQuery.find();
    } catch (e) {
      departmentsForEmployer = [];
      console.log('Could not fetch Departments', e);
      throw new Error('Could not fetch Departments');
    }

    return departmentsForEmployer;
  }

  
  static async getDepartment(departmentId: string) {
    const Department = Parse.Object.extend('Department');
    const departmentQuery = new Parse.Query(Department);

    try {
      const department = await departmentQuery.get(departmentId);
      return department;
    } catch (e) {
      console.log('Get Department did not complete sucessfully');
      throw new Error('Get Department did not complete sucessfully!');
    }
  }


  static async updateDepartment(departmentId: string, name: string, iconType?: string, employerId?: string) {
    const Employer = Parse.Object.extend('Employer');
    const Department = Parse.Object.extend('Department');
    const departmentQuery = new Parse.Query(Department);

    const tempEmployer = new Employer();
    tempEmployer.id = employerId;

    const department = await departmentQuery.get(departmentId);
    if (name) {
      department.set('name', name);
    }
    if (iconType) {
      department.set('iconType', iconType);
    } else {
      department.unset('iconType');
    }
    if (employerId) {
      department.set('employer', tempEmployer);
    }

    try {
      return await department.save();
    } catch (e) {
      console.log('Department update did not complete sucessfully');
      throw new Error('Department Update did not complete sucessfully!');
    }
  }

  // soft delete
  static async deleteDepartment(departmentId: string) {
    const Department = Parse.Object.extend('Department');
    const departmentQuery = new Parse.Query(Department);

    const department = await departmentQuery.get(departmentId);

    const currentMoment = moment().toDate();
    department.set('deletedAt', currentMoment);

    try {
      await department.save();
    } catch (e) {
      console.log('Department soft delete did not complete sucessfully');
    }
  }


  ////////////////////////
  /////////////////////////////////
  //////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////


  static async createLocation(
    name: string,
    employerId: string,
  ) {
    const Employer = Parse.Object.extend('Employer');
    const Location = Parse.Object.extend('Location');

    const employer = new Employer();
    employer.id = employerId;

    const employerLocation = new Location();
    employerLocation.set('employer', employer);
    employerLocation.set('name', name);

    console.log('save location..', name, employerId, employerLocation);

    try {
      const savedLocation = await employerLocation.save();
      console.log('location saved', savedLocation);
    } catch (e) {
      console.log('location save error', e);
      throw new Error(e);
    }
  }

  static async getLocations(employerId: string) {
    const Employer = Parse.Object.extend('Employer');
    const Location = Parse.Object.extend('Location');
    const locationQuery = new Parse.Query(Location);

    const employer = new Employer();
    employer.id = employerId;

    locationQuery.include('employer');
    locationQuery.doesNotExist('deletedAt');
    locationQuery.equalTo('employer', employer);
    let locationsForEmployer: any[] = [];

    try {
      locationsForEmployer = await locationQuery.find();
    } catch (e) {
      locationsForEmployer = [];
      console.log('Could not fetch Locations', e);
      throw new Error('Could not fetch Locations');
    }

    return locationsForEmployer;
  }

  static async getLocation(locationId: string) {
    const Location = Parse.Object.extend('Location');
    const locationQuery = new Parse.Query(Location);

    try {
      const location = await locationQuery.get(locationId);
      return location;
    } catch (e) {
      console.log('Get Location did not complete sucessfully');
      throw new Error('Get Location did not complete sucessfully!');
    }
  }


  static async updateLocation(locationId: string, name: string, employerId?: string) {
    const Employer = Parse.Object.extend('Employer');
    const Location = Parse.Object.extend('Location');
    const locationQuery = new Parse.Query(Location);

    const tempEmployer = new Employer();
    tempEmployer.id = employerId;

    const location = await locationQuery.get(locationId);
    if (name) {
      location.set('name', name);
    }
    if (employerId) {
      location.set('employer', tempEmployer);
    }

    try {
      return await location.save();
    } catch (e) {
      console.log('Location update did not complete sucessfully');
      throw new Error('Location Update did not complete sucessfully!');
    }
  }

  // soft delete
  static async deleteLocation(locationId: string) {
    const Location = Parse.Object.extend('Location');
    const locationQuery = new Parse.Query(Location);

    const location = await locationQuery.get(locationId);

    const currentMoment = moment().toDate();
    location.set('deletedAt', currentMoment);

    try {
      await location.save();
    } catch (e) {
      console.log('Department soft delete did not complete sucessfully');
    }
  }

    ////////////////////////
  /////////////////////////////////
  //////////////////////////////////////////////////
  /////////////////////////////////////////////////////////////////////


  static async createProperty(
    name: string,
    employerId: string,
  ) {
    const Employer = Parse.Object.extend('Employer');
    const Property = Parse.Object.extend('Property');

    const employer = new Employer();
    employer.id = employerId;

    const employerProperty = new Property();
    employerProperty.set('employer', employer);
    employerProperty.set('name', name);

    console.log('save property..', name, employerId, employerProperty);

    try {
      const savedProperty = await employerProperty.save();
      console.log('property saved', savedProperty);
    } catch (e) {
      console.log('property save error', e);
      throw new Error(e);
    }
  }

  static async getProperties(employerId: string) {
    const Employer = Parse.Object.extend('Employer');
    const Property = Parse.Object.extend('Property');
    const propertyQuery = new Parse.Query(Property);

    const employer = new Employer();
    employer.id = employerId;

    propertyQuery.include('employer');
    propertyQuery.doesNotExist('deletedAt');
    propertyQuery.equalTo('employer', employer);
    let propertiesForEmployer: any[] = [];

    try {
      propertiesForEmployer = await propertyQuery.find();
    } catch (e) {
      propertiesForEmployer = [];
      console.log('Could not fetch Properties', e);
      throw new Error('Could not fetch Properties');
    }

    return propertiesForEmployer;
  }
  
  static async getProperty(propertyId: string) {
    const Property = Parse.Object.extend('Property');
    const propertyQuery = new Parse.Query(Property);

    try {
      const property = await propertyQuery.get(propertyId);
      return property;
    } catch (e) {
      console.log('Get Property did not complete sucessfully');
      throw new Error('Get Property did not complete sucessfully!');
    }
  }


  static async updateProperty(propertyId: string, name: string, employerId?: string) {
    const Employer = Parse.Object.extend('Employer');
    const Property = Parse.Object.extend('Property');
    const propertyQuery = new Parse.Query(Property);

    const tempEmployer = new Employer();
    tempEmployer.id = employerId;

    const location = await propertyQuery.get(propertyId);
    if (name) {
      location.set('name', name);
    }
    if (employerId) {
      location.set('employer', tempEmployer);
    }

    try {
      return await location.save();
    } catch (e) {
      console.log('Property update did not complete sucessfully');
      throw new Error('Property Update did not complete sucessfully!');
    }
  }

  // soft delete
  static async deleteProperty(propertyId: string) {
    const Property = Parse.Object.extend('Property');
    const propertyQuery = new Parse.Query(Property);

    const property = await propertyQuery.get(propertyId);

    const currentMoment = moment().toDate();
    property.set('deletedAt', currentMoment);

    try {
      await property.save();
    } catch (e) {
      console.log('Property soft delete did not complete sucessfully');
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////

  static async inviteAdmin(params: AdminInviteParams) {
    await Parse.Cloud.run('inviteAdmin', params);
  }

  static async addEmployerAdmin(params: AddEmployerAdminParams) {
    return await Parse.Cloud.run('addEmployerAdmin', params);
  }

  static async addEmployerSuperAdmins(params: {
    userId: string;
    employerIds: string[];
  }) {
    return await Parse.Cloud.run('addEmployerSuperAdmins', params);
  }

  static async setUserAsEmployerSuperAdmin(params: AddEmployerAdminParams) {
    return await Parse.Cloud.run('setUserAsEmployerSuperAdmin', params);
  }

  static async removeEmployerAdmin(params: AddEmployerAdminParams) {
    return await Parse.Cloud.run('removeEmployerAdmin', params);
  }

  static async deleteEmployer(employerId: string, targetUserId: string) {
    const result = await Parse.Cloud.run('deleteEmployer', {
      employerId,
      targetUserId,
    });
    if (!result) {
      alert('Something went wrong trying to delete this user');
    }
  }

  
  static async deleteUserForEmployer(employerId: string, targetUserId: string) {
    const result = await Parse.Cloud.run('deleteUserForEmployer', {
      employerId,
      targetUserId,
    });
    if (!result) {
      alert('Something went wrong trying to delete this user');
    }
  }

  static async deleteStripeAccount(
    accountId: string,
    targets?: DeleteStripeTarget[]
  ): Promise<DeleteStripeAccountResponse> {
    const payload: Record<string, unknown> = { accountId };

    if (targets?.length) {
      payload.targets = targets;
    }

    const result = await Parse.Cloud.run('deleteStripeAccount', payload);

    if (!result) {
      throw new Error('deleteStripeAccount returned no result');
    }

    return result as DeleteStripeAccountResponse;
  }

  static async unlinkStripeAccount(
    userId: string
  ): Promise<UnlinkStripeAccountResponse> {
    const result = await Parse.Cloud.run('unlinkStripeAccount', {
      userId,
    });

    if (!result) {
      throw new Error('unlinkStripeAccount returned no result');
    }

    return result as UnlinkStripeAccountResponse;
  }

  static async getEmployers(params: any) {
    return await Parse.Cloud.run('getEmployers', params);
  }

  static async getEmployerAdmins(employerId: string) {
    return await Parse.Cloud.run('getEmployerAdmins', {
      employerId
    });
  }

  static async getEmployerPrivateForEmployer(
    employer: Parse.Object
  ): Promise<Parse.Object | undefined> {
    const EmployerPrivate = Parse.Object.extend('EmployerPrivate');
    const employerPrivateQuery = new Parse.Query(EmployerPrivate);
    return employerPrivateQuery.equalTo('employer', employer).first();
  }

  static async getIndividualAccount(
    userId: string
  ): Promise<IndividualAccount | undefined> {
    const queryUser = new Parse.User();
    queryUser.id = userId;

    const WorkerPrivate = Parse.Object.extend('WorkerPrivate');
    const workerPrivateQuery = new Parse.Query(WorkerPrivate);
    workerPrivateQuery
      .include('user')
      .include('worker')
      .equalTo('user', queryUser);
    const workerPrivate = await workerPrivateQuery.first();

    if (!workerPrivate) {
      return;
    }

    const employee: Partial<Employee> = {
      displayname: workerPrivate.get('worker').get('displayname'),
      parseObject: workerPrivate.get('worker'),
      parsePrivateObject: workerPrivate,
    };

    const parseUser = await workerPrivate.get('user');

    const user: User = {
      username: parseUser.get('username'),
      firstname: parseUser.get('firstname'),
      lastname: parseUser.get('lastname'),
      stripeLinked: !!parseUser.get('isStripeLinked'),
      parseObject: parseUser,
    };

    return {
      user,
      employee,
    };
  }

  static async getPaginatedTableFeatureFlag(): Promise<boolean> {
    return Parse.Cloud.run('getFeatureFlag', { name: 'new-pagination-table' });
  }

  static async setPaginatedTableFeatureFlag(): Promise<boolean> {
    return Parse.Cloud.run('setFeatureFlag', { name: 'new-pagination-table' });
  }

  static async getDailyTipSummaries(params?: {
    unixSeconds?: number;
    workerId?: string;
    workerIds?: string[];
    employerId?: string;
    employerIds?: string[];
  }) {
    try {
      const requestParams: {
        unixSeconds?: number;
        workerId?: string;
        workerIds?: string[];
        employerId?: string;
        employerIds?: string[];
      } = {};
      
      if (params?.unixSeconds) {
        requestParams.unixSeconds = params.unixSeconds;
      }
      if (params?.workerId) {
        requestParams.workerId = params.workerId;
      }
      if (params?.workerIds) {
        requestParams.workerIds = params.workerIds;
      }
      if (params?.employerId) {
        requestParams.employerId = params.employerId;
      }
      if (params?.employerIds) {
        requestParams.employerIds = params.employerIds;
      }
      
      const result = await Parse.Cloud.run('getDailyTipSummaries', requestParams);
      return result;
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getWeeklyTipSummaryForEmployer(params: {
    unixSeconds: number;
    employerId: string;
    workerIds?: string[];
  }) {
    try {
      const requestParams: {
        weekStartUnix: number;
        employerId: string;
        workerIds?: string[];
      } = {
        weekStartUnix: params.unixSeconds,
        employerId: params.employerId,
      };
      
      if (params.workerIds) {
        requestParams.workerIds = params.workerIds;
      }
      
      const result = await Parse.Cloud.run('getWeeklyTipSummaryForEmployer', requestParams);
      return result;
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  /** Workers with connection status for a date range. startDate/endDate as YYYY-MM-DD. */
  static async getWorkersWithConnectionStatus(params: {
    startDate: string;
    endDate: string;
    timezone?: string;
    reportComplexity?: 'simple' | 'expanded';
  }) {
    try {
      const requestParams = {
        startDate: params.startDate,
        endDate: params.endDate,
        timezone: params.timezone ?? 'UTC',
        reportComplexity: params.reportComplexity ?? 'simple',
      };
      return await Parse.Cloud.run('getWorkersWithConnectionStatus', requestParams);
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async collectStatistics() {
    try {
      return await Parse.Cloud.run('collectStatistics');
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getTotalTipChartData() {
    try {
      return await Parse.Cloud.run('getTotalTipChartData');
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getDailyTipChartData() {
    try {
      return await Parse.Cloud.run('getDailyTipChartData');
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  /** Tip summary CSV for a period. Response has csvContent (string) or csvBase64 when returnBase64 is true. */
  static async getTipSummaryCSVForPeriod(params: {
    periodType: 'day' | 'week' | 'month';
    timezone?: string;
    returnBase64?: boolean;
    currencyCode?: string;
  } & (
    | { periodType: 'day'; workerId: string; unixSeconds?: number; employerId?: never; workerIds?: never, currencyCode?: string }
    | { periodType: 'day'; employerId: string; unixSeconds: number; workerIds?: string[]; workerId?: never, currencyCode?: string }
    | { periodType: 'week'; employerId: string; weekStartUnix?: number, currencyCode?: string }
    | { periodType: 'month'; employerId: string; year: number; month: number, currencyCode?: string }
  )): Promise<{ csvContent?: string; csvBase64?: string }> {
    try {
      const base: {
        periodType: string;
        timezone?: string;
        returnBase64?: boolean;
        currencyCode?: string;
      } = {
        periodType: params.periodType,
      };
      if (params.timezone) base.timezone = params.timezone;
      if (params.returnBase64 !== undefined) base.returnBase64 = params.returnBase64;
      if (params.currencyCode) base.currencyCode = params.currencyCode;

      if (params.periodType === 'day') {
        if (typeof (params as any).employerId === 'string') {
          const p = params as Extract<typeof params, { periodType: 'day'; employerId: string; unixSeconds: number }>;
          const requestParams: {
            periodType: string;
            timezone?: string;
            returnBase64?: boolean;
            employerId: string;
            unixSeconds: number;
            workerIds?: string[];
          } = { ...base, employerId: p.employerId, unixSeconds: p.unixSeconds };
          if ((p as any).workerIds) requestParams.workerIds = (p as any).workerIds;
          return await Parse.Cloud.run('getTipSummaryCSVForPeriod', requestParams);
        }
        const requestParams = { ...base, workerId: params.workerId };
        if (params.unixSeconds !== undefined) (requestParams as any).unixSeconds = params.unixSeconds;
        return await Parse.Cloud.run('getTipSummaryCSVForPeriod', requestParams);
      }
      if (params.periodType === 'week') {
        const requestParams = { ...base, employerId: params.employerId };
        if (params.weekStartUnix !== undefined) (requestParams as any).weekStartUnix = params.weekStartUnix;
        return await Parse.Cloud.run('getTipSummaryCSVForPeriod', requestParams);
      }
      // month
      const requestParams = {
        ...base,
        employerId: params.employerId,
        year: params.year,
        month: params.month,
      };
      return await Parse.Cloud.run('getTipSummaryCSVForPeriod', requestParams);
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getMonthlyFeedbackSummary(params: {
    targetType: 'worker' | 'department' | 'employer';
    targetId: string;
    year: number;
    month: number; // 1-12
  }) {
    try {
      const result = await Parse.Cloud.run('getMonthlyFeedbackSummary', params);
      return result;
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }
    
  static async updateUser(params: {
    objectId: string;
    email: string;
    firstname: string;
    lastname: string;
    jobTitle?: string;
    phone?: string;
  }) {
    return await Parse.Cloud.run('updateUser', params);
  }

  static async getAuditLogs(params: {
    pageNumber?: number;
    pageSize?: number;
    searchBy?: AuditLogSearchBy;
  }): Promise<AuditLogListResponse> {
    try {
      return await Parse.Cloud.run('getAuditLogs', params);
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }

  static async getAuditLogById(objectId: string): Promise<AuditLogEntry> {
    try {
      return await Parse.Cloud.run('getAuditLogById', { objectId });
    } catch (error) {
      ApiService.handleParseError(error);
      throw error;
    }
  }
}
